import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import api from './services/api';
import { AuthContext } from './contexts/AuthContext';
import Login from './pages/Login';
import Storefront from './pages/Storefront';
import Dashboard from './pages/Dashboard';
import POS from './pages/POS';
import Products from './pages/Products';
import Categories from './pages/Categories';
import Inventory from './pages/Inventory';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import Suppliers from './pages/Suppliers';
import Purchases from './pages/Purchases';
import Transfers from './pages/Transfers';
import InventoryInsights from './pages/InventoryInsights';
import Promotions from './pages/Promotions';
import InventoryAudit from './pages/InventoryAudit';
import AuditTrail from './pages/AuditTrail';
import SalesHistory from './pages/SalesHistory';
import CustomerDisplay from './pages/CustomerDisplay';
import ReceiptPage from './pages/ReceiptPage';
import GiftCards from './pages/GiftCards';
import Attendance from './pages/Attendance';
import Layout from './components/Layout';
import LicenseSetup from './pages/LicenseSetup';

import { ShiftProvider } from './contexts/ShiftContext';
import { SettingsProvider } from './contexts/SettingsContext';

const normalizeIdentity = (value = '') => {
  let raw = String(value).trim().toLowerCase();
  raw = raw.replace(/^https?:\/\//, '');

  const [hostPart, ...pathParts] = raw.split('/');
  const host = hostPart.replace(/:\d+$/, '');
  const path = pathParts.join('/').replace(/^\/+|\/+$/g, '');

  return path ? `${host}/${path}` : host;
};

function LicenseGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [isLicensed, setIsLicensed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkLicense = async () => {
      try {
        const res = await api.get('/license/info');
        const licensedDomain = normalizeIdentity(res.data?.licenseDomain || '');
        const basePath = String(process.env.PUBLIC_URL || '').replace(/^\/+|\/+$/g, '');
        const currentIdentity = normalizeIdentity(
          `${window.location.hostname}${basePath ? `/${basePath}` : ''}`
        );
        const isDevHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        const domainMatches = !licensedDomain || isDevHost || currentIdentity === licensedDomain;

        if (!cancelled) {
          setIsLicensed(Boolean(res.data?.isLicensed) && domainMatches);
        }
      } catch (err) {
        if (!cancelled) {
          setIsLicensed(false);
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    };

    checkLicense();

    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-main)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: 'var(--brand)' }}></div>
      </div>
    );
  }

  return isLicensed ? children : <Navigate to="/license-setup" replace />;
}

function PrivateRoute({ children }) {
  const { user } = useContext(AuthContext);
  return user ? children : <Navigate to="/login" />;
}

function PermissionRoute({ children, permission }) {
  const { user } = useContext(AuthContext);
  if (user?.role === 'admin') return children;
  if (user?.permissions?.includes(permission)) return children;
  return <Navigate to="/app" />;
}

export default function App() {
  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const routerBaseName = isLocalHost ? '' : (process.env.PUBLIC_URL || '');

  return (
    <BrowserRouter basename={routerBaseName}>
      <SettingsProvider>
        <ShiftProvider>
          <Routes>
            <Route path="/" element={<LicenseGate><Storefront /></LicenseGate>} />
            <Route path="/login" element={<LicenseGate><Login /></LicenseGate>} />
            <Route path="/license-setup" element={<LicenseSetup />} />
            <Route path="/customer-display" element={<CustomerDisplay />} />
            <Route path="/app" element={<LicenseGate><PrivateRoute><Layout /></PrivateRoute></LicenseGate>}>
              <Route index element={<PermissionRoute permission="dashboard"><Dashboard /></PermissionRoute>} />
              <Route path="pos" element={<PermissionRoute permission="pos"><POS /></PermissionRoute>} />
              <Route path="products" element={<PermissionRoute permission="products"><Products /></PermissionRoute>} />
              <Route path="categories" element={<PermissionRoute permission="products"><Categories /></PermissionRoute>} />
              <Route path="inventory" element={<PermissionRoute permission="inventory"><Inventory /></PermissionRoute>} />
              <Route path="customers" element={<PermissionRoute permission="customers"><Customers /></PermissionRoute>} />
              <Route path="suppliers" element={<PermissionRoute permission="vendors"><Suppliers /></PermissionRoute>} />
              <Route path="purchases" element={<PermissionRoute permission="purchases"><Purchases /></PermissionRoute>} />
              <Route path="transfers" element={<PermissionRoute permission="transfers"><Transfers /></PermissionRoute>} />
              <Route path="inventory-insights" element={<PermissionRoute permission="inventory"><InventoryInsights /></PermissionRoute>} />
              <Route path="promotions" element={<PermissionRoute permission="marketing"><Promotions /></PermissionRoute>} />
              <Route path="inventory-audit" element={<PermissionRoute permission="inventory"><InventoryAudit /></PermissionRoute>} />
              <Route path="audit-trail" element={<PermissionRoute permission="admin_access"><AuditTrail /></PermissionRoute>} />
              <Route path="reports" element={<PermissionRoute permission="reports"><Reports /></PermissionRoute>} />
              <Route path="sales-history" element={<PermissionRoute permission="reports"><SalesHistory /></PermissionRoute>} />
              <Route path="gift-cards" element={<PermissionRoute permission="marketing"><GiftCards /></PermissionRoute>} />
              <Route path="attendance" element={<PermissionRoute permission="dashboard"><Attendance /></PermissionRoute>} />
              <Route path="admin" element={<PermissionRoute permission="settings"><Admin /></PermissionRoute>} />
              <Route path="receipt/:saleId" element={<ReceiptPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </ShiftProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}
